/* eslint-disable react-hooks/exhaustive-deps */
import AuditSheet from './components/AuditSheet';
import AuditSheetFooter from './components/AuditSheetFooter';
import Topbar from './components/Topbar';
import {
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  useBoolean,
  VStack,
  Text
} from '@chakra-ui/react';
import AppColors from '../../constants/AppColors';
import { withProvider } from '../../hoc/withProvider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AuditSheetProvider, {
  AuditSheetContext,
} from '../../providers/AuditSheetProvider';
import { createEmptyObject } from './components/createEmptyObject';
import { sanitize } from './components/sanitize';
import SubmitModal from './components/SubmitModal';
import ROUTES from '../../constants/Routes';
import { useToastr } from '@laxmimanogna/code-quick-components';
import { successToast } from '../../components/successToast';
import { submitRule } from './components/submitRules';
import CommentBox from './components/CommentBox';
import html2canvas from 'html2canvas';
import {
  arrangeData,
  createEmptyColumn,
  prepareUpdatePayload,
} from '../../utils/auditsheet.utils';
import { AuthContext } from '../../providers/AuthProvider';
import { READ_ONLY_SHEET_STATUSES } from './constants';
import { AccessDenied } from './components/AccessDenied';

const AuditSheetContainer = ({ view, batch}) => {
  const auditSheetCount = Math.ceil(window.innerHeight / 24);
  const location = useLocation();

  // refs
  const domEl = useRef(null);
  const topbar = useRef(null);
  const footer = useRef(null);
  const notesDrawerRef = useRef(null);

  // Booleans
  const [isLoading, ilState] = useBoolean(false);
  const [isOpen, ioState] = useBoolean(false);
  const [isCommentOpen, icoState] = useBoolean(false);

  // other hooks
  const { uploadId, batchId } = useParams(); // chartId
  const navigate = useNavigate();
  const toast = useToastr();

  // states
  const [lastSaved, setLastSaved] = useState('');
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [sheets, setSheets] = useState([
    {
      index: 1,
      sheet_name: 'Sheet1',
      data: createEmptyObject(auditSheetCount),
    },
  ]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [metaData, setMetaData] = useState({});
  const [isSubmitDisabled, setSubmitDisabled] = useState(true);
  const [batchName, setBatchName] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  //context
  const auditSheetContext = useContext(AuditSheetContext);
  const authContext = useContext(AuthContext)
  const {
    getAuditUpload,
    getAuditSheet,
    currentUpload,
    auditSheetColumnsKeys,
    getBatchAuditSheet,
    isAccessDenied,
    isAuditSheetLoading
  } = auditSheetContext;
  const { getIndustryCodes, getICDCodes, getModifiers, getUsers, users } = auditSheetContext;
  const { getAllUsers, getCommentFlag, providerOptions } = auditSheetContext;
  const { getComments, setSheetData, getRebuttalSubmitValidation, allComments, setChartBatchId } = auditSheetContext;
  const [updatingAuditSheet, uasState] = useBoolean(false);
  const [outstandingAuditSheetUpdate, setOutstandingAuditSheetUpdate] = useState(null);
  const sheetNames = sheets.map(s => s?.sheet_name);

  // get auditsheet user data
  const getUpload = async () => {
    if (batch) {
      if (!view) {
        navigate(`${location.pathname}/view`, { replace: true });
        view = true;
      }
      getSheet();
      return;
    }
    if (uploadId) {
      const res = await getAuditUpload(uploadId);
      if (res.error) {
        setErrMsg(res.error);
        return;
      }
      setLastSaved(res.updated_date);
      if (!view) {     
        // for read onlt files and for editable files not assigned to the manager redirect to view only page
        if (READ_ONLY_SHEET_STATUSES.includes(res?.status) || res?.assigned_auditor.id !== authContext.currentUser.id) {
          navigate(`${location.pathname}/view`, { replace: true });
          view = true;
          return;
        }
        auditSheetContext.monitorAuditHour(uploadId);
      }
    }
  };

  // Build sheetData map: { rowId: rowNumber (1-based) } for location display in notes drawer
  useEffect(() => {
    const map = {};
    sheets.forEach(sheet => {
      sheet.data.forEach((row, idx) => {
        if (row.id != null) {
          map[row.id] = idx + 1;
        }
      });
    });
    setSheetData(map);
  }, [sheets]);

  const onCommentClick = async (rowIndex, rowData) => {
    if (!rowData?.id) return;
    setMetaData({ rowIndex, selectedRowData: rowData });
    await getComments(uploadId, { row: rowData.id });
    icoState.on();
  };

  const onSelectionEnd = (rowData) => {
    setSelectedRowData(rowData);
  };

  const onZoom = value => {
    let temp = 1;
    if (value >= 50) {
      temp = (2 * value) / 100;
    } else if (value === 0) {
      temp = 0.3;
    } else {
      temp = (value / 100) * 2;
    }
    setScale(temp);
  };

  // sheets Data || fetch data
  const getSheet = async () => {
    if (!isAccessDenied || batch) {
      if (uploadId) {
        const response = await getAuditSheet(uploadId, currentUpload);
        const sheetBatchId = response[0]?.batch_id || response[0]?.batch;
        if (sheetBatchId) setChartBatchId(sheetBatchId);
        
        if (response?.length) {
          const updated = response.map((res, i) => {
            const _reDestructuredData = createEmptyColumn(res.data);

            const reDestructuredData = _reDestructuredData.map(sd => {
              const tempObj = { ...sd, ...sd.additional_attributes };
              tempObj['audited_cpt'] = tempObj['audited_code'];
              delete tempObj.additional_attributes;
              delete tempObj?.audited_code;

              if (tempObj?.enc_dt) {
                  const parts = tempObj.enc_dt.split('-');
                  const year = parseInt(parts[0], 10);
                  const month = parseInt(parts[1], 10) - 1;
                  const day = parseInt(parts[2], 10);
                  const parsedDate = new Date(year, month, day);
            
                  if (!isNaN(parsedDate.getTime())) {
                    const formattedDate = `${(parsedDate.getMonth() + 1).toString().padStart(2, '0')}-${parsedDate.getDate().toString().padStart(2, '0')}-${parsedDate.getFullYear()}`;
                    tempObj.enc_dt = formattedDate;
                  }
              }
              if (!tempObj.hasOwnProperty('icd_agree')){
                tempObj['icd_agree'] = null
              }
              if (!tempObj.hasOwnProperty('icd_disagree')){
                tempObj['icd_disagree'] = null
              }

              return tempObj;
            });

            const _arrangeData = arrangeData(reDestructuredData);

            const updatedData = [
              ..._arrangeData,
              ...createEmptyObject(auditSheetCount - res.data.length),
            ];
            return {
              ...res,
              index: i + 1,
              data: updatedData,
            };
          });
          setSheets(updated);
        }
      } else if (batch) {
          const response = await getBatchAuditSheet(batchId);
          if (response?.error) {
            setErrMsg(response.error);
            return;
          }
    
          if (response.length) {
            setBatchName(response[0].batch_name);
            const updated = response.map((res, i) => {
    
              const _reDestructuredData = createEmptyColumn(res.data);
    
              const reDestructuredData = _reDestructuredData.map(sd => {
                const tempObj = { ...sd, ...sd.additional_attributes };
                tempObj['audited_cpt'] = tempObj['audited_code'];
                delete tempObj.additional_attributes;
                delete tempObj?.audited_code;
    
                if (tempObj?.enc_dt) {
                  const parts = tempObj.enc_dt.split('-');
                  const year = parseInt(parts[0], 10);
                  const month = parseInt(parts[1], 10) - 1;
                  const day = parseInt(parts[2], 10);
                  const parsedDate = new Date(year, month, day);
    
                  if (!isNaN(parsedDate.getTime())) {
                    const formattedDate = `${(parsedDate.getMonth() + 1)
                      .toString()
                      .padStart(2, '0')}-${parsedDate
                      .getDate()
                      .toString()
                      .padStart(2, '0')}-${parsedDate.getFullYear()}`;
                    tempObj.enc_dt = formattedDate;
                  }
                }
                if (!tempObj.hasOwnProperty('icd_agree')){
                  tempObj['icd_agree'] = null
                }
                if (!tempObj.hasOwnProperty('icd_disagree')){
                  tempObj['icd_disagree'] = null
                }
                return tempObj;
              });
    
    
              const _arrangeData = arrangeData(reDestructuredData);
    
    
              const updatedData = [
                ..._arrangeData,
                ...createEmptyObject(auditSheetCount - res.data.length),
              ];
              return {
                ...res,
                index: i + 1,
                data: updatedData,
              };
            });
    
            setSheets(updated);
            disableSubmitFunc(response)
    
          }
        };
      }
  };

  useEffect(() => {
    if(!isAccessDenied){
      auditSheetContext.fetchResponseAuditIssues();
    }
    if(!isAccessDenied && !view){
      getIndustryCodes();
      getICDCodes();
      getModifiers();
      getUsers()
      getAllUsers()
      if (uploadId) {
        auditSheetContext.fetchProviderDropdown(uploadId)
      }
    }
  }, [isAccessDenied]);

  useEffect(() => {
    if(!isAccessDenied){
      if (uploadId || batchId) {
        getCommentFlag({ uploadId, batchId })
      }
    }
  }, [uploadId, batchId, isAccessDenied])

  useEffect(() => {
      getUpload();
    }, []);

  useEffect(()=>{
    // performing get sheet for single audit sheet view because batch already has their sheet called
    if (!isAccessDenied && uploadId) {
      getSheet();
    }

  },[uploadId, isAccessDenied])

  useEffect(() => {
    /**
     * sanitize() will add old_rendering and asign as id to rendering and remove empty rows
     */

    const sanitizedSheet = sanitize(sheets, providerOptions);

    disableSubmitFunc(sanitizedSheet);
  }, [sheets]);

  // functions and handlers

  function disableSubmitFunc(sanitizedSheet) {
    // For REBUTTAL IN PROGRESS, skip data validation — audit was already validated
    // when first submitted. Auditor/manager just needs to respond to rebuttals and re-submit.
    if (currentUpload?.status === 'REBUTTAL IN PROGRESS') {
      setSubmitDisabled(false);
      return;
    }

    const isEverySanitizedSheetEmpty = sanitizedSheet.every(
      sheet => sheet.data.length === 0
    );

    if (isEverySanitizedSheetEmpty) {
      setSubmitDisabled(true);
    } else {
      const isSubmitable = submitRule(sanitizedSheet);
      setSubmitDisabled(isSubmitable);
    }
  }

  // on left arrow click
  function onLeftNavigate() {
    if (activeSheetIndex > 0) {
      setActiveSheetIndex(activeSheetIndex - 1);
    }
  }

  //  on right arrow click
  function onRightNavigate() {
    if (activeSheetIndex < sheets.length - 1) {
      setActiveSheetIndex(activeSheetIndex + 1);
    }
  }

  function updateSheet(sheetData) {
    sheets[activeSheetIndex].data = sheetData;
    setSheets([...sheets]);
  }

  const removeRows = rowIndexes => {
    const sortedIndexes = [...rowIndexes].sort((a, b) => b - a); // bottom-up so indices stay correct
    const data = [...sheets[activeSheetIndex].data];
    sortedIndexes.forEach(i => data.splice(i, 1));
    sheets[activeSheetIndex].data = data;
    setSheets([...sheets]);
  };

  function onOpenNotes(obj) {
    if (obj && Object.keys(obj).length) {
      if (obj.all) {
        delete auditSheetContext.commentFilters.sheet_name;
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          page: 1,
        });
      } else if (obj.current) {
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          sheet_name: sheets[activeSheetIndex].sheet_name,
          page: 1,
        });
      }
    } else {
      auditSheetContext.setCommentFilters({
        ...auditSheetContext.commentFilters,
        uploadId,
        batchId,
        sheet_name: sheets[activeSheetIndex].sheet_name,
        reverse: true,
        page: 1,
      });
    }
  }

  // async functions

  async function downloadImage() {
    // download image
    function dataURIToBlob(dataURI) {
      const splitDataURI = dataURI.split(',');
      const byteString =
        splitDataURI[0].indexOf('base64') >= 0
          ? atob(splitDataURI[1])
          : decodeURI(splitDataURI[1]);
      const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

      const ia = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++)
        ia[i] = byteString.charCodeAt(i);

      return new Blob([ia], { type: mimeString });
    }
    const canvas = await html2canvas(domEl.current);
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    return dataURIToBlob(dataUrl);
  }

  const updateCurrentAuditImage = async () => {
    try {
      //submit screenshot
      const file = await downloadImage();
      const fd = new FormData();
      fd.append('image', file);
      return await auditSheetContext.recentAuditImageUpload(uploadId, fd);
    } catch (err) {

    }
  }

  async function onAuditSubmit() {
    // await auditSheetContext.monitorAuditHour(uploadId);
    await updateCurrentAuditImage();
    const sanitizedSheet = sanitize(sheets, providerOptions);

    const payload = prepareUpdatePayload(sanitizedSheet);

    //  const payload= payloadreStructure(sanitizedSheet)

    const response = await auditSheetContext.updateAuditSheet(
      payload, //sanitizedSheet,
      uploadId
    );

    if (response) {
      ioState.off();
      navigate(ROUTES.MY_AUDITS);
      const toastProps = {
        header: 'Success!',
        description: `${'Document submitted for review.'}`,
      };
      successToast(toast, toastProps);
    }
  }

  const prepareDeltaPayload = (sheet, key) => {
    if (!sheet[key]?.length) return sheet[key];

    // 1️Sanitize
    const sanitized = sanitize(
      [{ ...sheet, data: sheet[key] }],
      providerOptions
    );
    // Prepare payload (same structure as sanitize)
    const payloadResult = prepareUpdatePayload(sanitized);
    // since both return array of sheets
    return payloadResult[0].data;
  };

  /**
   *
   * @param {Array} sheetParams
   * @returns
   */

  async function updateAuditSheet(sheetParams) {
    if (updatingAuditSheet) {
      setOutstandingAuditSheetUpdate({sheetParams: sheetParams});
      return;
    }

    // Check if this is a delta update (has deltaPayload)
    if (sheetParams?.deltaPayload) {
      try {
        uasState.on();
        const sheet = sheetParams.deltaPayload;
        const updatedDeltaPayload = {
          ...sheet,
          created: prepareDeltaPayload(sheet, 'created'),
          updated: prepareDeltaPayload(sheet, 'updated'),
        };
        const response = await auditSheetContext.createAuditSheetDelta(
          updatedDeltaPayload, 
          uploadId
        );
        // Update local sheet data with new IDs if any rows were created
        if (response?.created_ids && response.created_ids.length > 0) {
          const currentSheet = sheets[activeSheetIndex];
          const updatedData = currentSheet.data.map(row => {
            // Find rows that need ID assignment (rows with id === null)
            if (row.id === null) {
              // Match by position or other identifier
              const createdIndex = sheetParams.deltaPayload.created.findIndex(
                createdRow => createdRow === row
              );
              if (createdIndex !== -1 && response.created_ids[createdIndex]) {
                return { ...row, id: response.created_ids[createdIndex] };
              }
            }
            return row;
          });
          
          sheets[activeSheetIndex].data = updatedData;
          setSheets([...sheets]);
        }
        
        uasState.off();
        setLastSaved(new Date());
        const currentSheet = sheets[activeSheetIndex];
        const sorted = [...currentSheet.data].sort((a, b) => {
          if (a.id === null && b.id !== null) return 1;
          if (a.id !== null && b.id === null) return -1;
          if (a.id !== null && b.id !== null) return a.id - b.id; // sort by id ascending
          return 0;
        });
        sheets[activeSheetIndex].data = sorted;
        setSheets([...sheets]);
        
      } catch (err) {
        uasState.off();
        console.error('Delta update failed:', err);
      }
      return;
    }

    let sanitizedSheet = [];
    if (sheetParams) {
      sheets[activeSheetIndex] = sheetParams;
      sanitizedSheet = sanitize(sheets, providerOptions);
    } else {
      sanitizedSheet = sanitize(sheets, providerOptions);
    }

    const payload = prepareUpdatePayload(sanitizedSheet);

    try {
      // const reStructurePayload = payloadreStructure(sanitizedSheet);

      uasState.on();
      await auditSheetContext.createAuditSheet(payload, uploadId);
      await getSheet();

      uasState.off();

      // saving screenshot
      await updateCurrentAuditImage();
      
      setLastSaved(new Date());
    } catch (err) {
      uasState.off();
    }
  }

  useEffect(() => {
    if (!updatingAuditSheet && outstandingAuditSheetUpdate) {
      updateAuditSheet(...Object.values(outstandingAuditSheetUpdate));
      setOutstandingAuditSheetUpdate(null);
    }
  }, [updatingAuditSheet])

  async function addNewSheet() {
    const updatedSheets = [
      ...sheets,
      {
        index: sheets.length + 1,
        data: createEmptyObject(auditSheetCount),
        sheet_name: `Sheet${sheets.length + 1}`,
      },
    ];
    setSheets([...updatedSheets]);
    setActiveSheetIndex(sheets.length);
  }

  async function changeSheetName(index, name) {
    sheets[index] = { ...sheets[index], sheet_name: name };

    setSheets([...sheets]);

    const sanitizedSheet = sanitize(sheets, providerOptions);
    await auditSheetContext.createAuditSheet(sanitizedSheet, uploadId);
  }

  async function onDeleteSheet(index) {
    if (sheets.length > 1) {
      let sheetIndex = 1;
      let newSheet = [];
      sheets.forEach((p, i) => {
        if (index !== i) {
          newSheet.push({ ...p, index: sheetIndex });
          sheetIndex = sheetIndex + 1;
        }
      });
      setSheets([...newSheet]);
      if (activeSheetIndex === index && index > 0) {
        const data = activeSheetIndex - 1;
        setActiveSheetIndex(data);
      }
      const sanitizedSheet = sanitize(newSheet, providerOptions);
      await auditSheetContext.createAuditSheet(sanitizedSheet, uploadId);
    }
  }


  const [coords, setCoords] = useState({
    x: 0,
    y: 0,
  });

  function handleSheetClick(e) {
    setCoords({
      x:e.clientX,
      y:e.clientY
    })
  }

  // renders
  const renderComment = () => {
    return (
      <CommentBox
        showMenu={isCommentOpen}
        metaData={metaData}
        closeComment={() => icoState.off()}
        coords={coords}
      />
    );
  };

  const renederToolBar = () => {
    return (
      <Box w={'100%'} ref={topbar} top={0}>
        <Topbar
          onAuditSubmit={async () => {
            if (currentUpload?.status === 'REBUTTAL IN PROGRESS') {
              const hasOpenRebuttals = allComments.some(c => {
                if (c.parent || c.action !== 'REBUTTAL') return false;
                const replies = c.reply_comments || [];
                let status = 'Open';
                for (const r of replies) {
                  if (r.action === 'REBUTTAL-REJECTED') status = 'Rejected';
                  else if (r.action === 'REBUTTAL-RESOLVED') status = 'Closed';
                  else if (r.action === 'REBUTTAL-ACCEPTED') status = 'Accepted';
                  else if (r.action === 'RE-OPENED') status = 'Accepted';
                }
                return status === 'Accepted';
              });
              if (hasOpenRebuttals) {
                toast.showError({ description: 'Please close all accepted rebuttals before submitting.' });
                return;
              }
              const validation = await getRebuttalSubmitValidation(uploadId);
              if (!validation?.can_submit) {
                toast.showError({ description: validation?.message || 'Close all accepted rebuttals before submitting.' });
                return;
              }
            }
            ioState.on();
          }}
          batchId={batchId}
          batchName={batchName}
          sheet={sheets[activeSheetIndex]}
          updateAuditSheet={async params => {
            await updateAuditSheet(params);
          }}
          view={view}
          disableSubmit={isSubmitDisabled}
          lastSaved={lastSaved}
          onChange={on => {
            if (on) {
              ilState.on();
            } else {
              ilState.off();
            }
          }}
          onOpenNotes={onOpenNotes}
          notesDrawerRef={notesDrawerRef}
        />
      </Box>
    );
  };





  const renderAuditSheet = () => (
    <Box id="domEl" ref={domEl} onClick={handleSheetClick}>
      <AuditSheet
        height={
          window.innerHeight -
          topbar?.current?.clientHeight -
          footer?.current?.clientHeight
        }
        scale={scale}
        sheet={sheets[activeSheetIndex]}
        uploadId={uploadId}
        view={view}
        batch={batch}
        updateAuditSheet={updateAuditSheet} // create audit sheet(POST) and getSheets and update sheet
        updateSheet={updateSheet} // update the data in setState(setSheet)
        onCommentClick={onCommentClick}
        onSelectionEnd={onSelectionEnd}
        removeRows={removeRows}
        closeComment={() => icoState.off()}
        getSheet={getSheet}
      />
    </Box>
  );

  const renderFooter = () => {
    return (
      <Box w={'100%'} ref={footer} position={'fixed'} bottom={0}>
        <AuditSheetFooter
          onSheetChange={setActiveSheetIndex}
          sheets={sheetNames}
          activeSheetIndex={activeSheetIndex}
          addNewSheet={addNewSheet}
          changeSheetName={changeSheetName}
          onLeftNavigate={onLeftNavigate}
          onRightNavigate={onRightNavigate}
          onZoom={onZoom}
          onDeleteSheet={onDeleteSheet}
        />
      </Box>
    );
  };

  const renderSubmitModal = () => {
    return (
      <SubmitModal
        isOpen={isOpen}
        onClose={() => ioState.off()}
        onAuditSubmit={onAuditSubmit}
      />
    );
  };

  const renderLoader = () => {
    return (
      <Modal size={'sm'} isOpen={isLoading} isCentered bgColor={'transparent'}>
        <ModalOverlay style={{ backgroundColor: AppColors.lightSkyBlue }} />
        <ModalContent bgColor={'transparent'} shadow={'none'}>
          <ModalBody style={{ alignSelf: 'center' }}>
            <Spinner />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  };

  const renderMainContent = () => {
    return (
      (isAuditSheetLoading ? (
            <VStack height="100vh" justify="center" align="center">
              <Spinner size="xl" />
              <Text>Loading...</Text>
            </VStack>
          ) :
          (!isAccessDenied ? (
      <Box bgColor={AppColors.bgColor}>
        {renederToolBar()}

        {renderAuditSheet()}

        {renderFooter()}

        {renderSubmitModal()}

        {renderLoader()}

        {renderComment()}
      </Box>
          ): <AccessDenied
                  errMsg={errMsg}/>
        ))
    );
  };

  return renderMainContent();
};
export default withProvider(AuditSheetProvider, AuditSheetContainer);